import { Primitive } from "@radix-ui/react-primitive";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../utils";
import { Label } from "../Label";
import { Error } from "../fields/Error";

const fontVariants = cva("", {
  variants: {
    font: {
      light: "tw-font-light",
      normal: "tw-font-normal",
      medium: "tw-font-medium",
      semibold: "tw-font-semibold",
      bold: "tw-font-bold",
    },
  },
  defaultVariants: {
    font: "semibold",
  },
});

const textVariants = cva("", {
  variants: {
    size: {
      sm: ["tw-text-xs"],
      md: ["tw-text-sm"],
      lg: ["tw-text-base"],
      xl: ["tw-text-lg"],
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const inputVariants = cva(
  "tw-w-full tw-border tw-border-gray-200 tw-bg-white tw-p-2 tw-px-4 tw-shadow-md placeholder:tw-text-gray-600/30 focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-primary disabled:tw-cursor-not-allowed disabled:tw-opacity-50 md:tw-rounded lg:tw-rounded",
  {
    variants: {
      size: {
        sm: "tw-h-8",
        md: "tw-h-10",
        lg: "tw-h-12",
        xl: "tw-h-16",
      },
      variant: {
        default: "tw-border tw-bg-white hover:tw-border-primary/30",
        error: "tw-border-red-500",
        success: "tw-border-green-500",
      },
    },
    defaultVariants: {
      size: "md",
      variant: "default",
    },
  },
);

type InputVariants = VariantProps<typeof inputVariants> &
  VariantProps<typeof fontVariants>;

export interface InputProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Primitive.input>, "size">,
    InputVariants {
  rightElement?: React.ReactNode;
  leftElement?: React.ReactNode;
  error?: string;
  label?: React.ReactNode;
}

export const Input = React.forwardRef<
  React.ElementRef<typeof Primitive.input>,
  InputProps
>(
  (
    {
      className,
      size,
      variant,
      rightElement,
      error,
      font,
      leftElement,
      label,
      ...props
    },
    ref,
  ) => (
    <>
      {label && <Label className="tw-font-semibold">{label}</Label>}
      <div
        className={cn(
          "tw-relative tw-text-sm tw-text-black",
          fontVariants({ font }),
          textVariants({ size }),
          { "tw-text-red-200": !!error },
        )}
      >
        <Primitive.input
          ref={ref}
          className={cn(
            inputVariants({ size, variant }),
            "tw-text-base",
            "tw-peer",
            {
              "tw-pl-10": leftElement,
              "tw-pr-6": rightElement,
              "tw-border-red-200 focus-visible:tw-border-red-200 focus-visible:tw-ring-red-200":
                !!error,
            },
            className,
          )}
          {...props}
        />
        {leftElement && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-start-0 tw-flex tw-items-center tw-justify-center tw-ps-4 tw-font-semibold peer-disabled:tw-opacity-50">
            {leftElement}
          </div>
        )}
        {rightElement && (
          <div
            className={cn(
              "tw-pointer-events-none tw-invisible tw-absolute tw-inset-y-0 tw-end-0 tw-flex tw-items-center tw-justify-center tw-pe-4 peer-focus:tw-visible peer-disabled:tw-opacity-50 tw-font-semibold",
              { "tw-visible": props.value !== "" },
            )}
          >
            {rightElement}
          </div>
        )}
      </div>
      {error && <Error className="tw-ml-1 tw-mt-1">{error}</Error>}
    </>
  ),
);
